import { BaseService } from './BaseService';
import { 
  LoginCredentials, 
  LoginResponse, 
  User, 
  ChangePasswordRequest,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  VerifyResetCodeRequest,
  VerifyResetCodeResponse,
  ResetPasswordRequest,
  ResetPasswordResponse
} from '@/types/auth';
import { Role } from '@/types/enums';
import { ApiResponse } from '@/types';

/**
 * Servicio de autenticación que extiende BaseService
 * Maneja login, logout, gestión de tokens y reset de contraseña
 */
class AuthService extends BaseService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'auth_user';

  constructor() {
    super('/auth');
  }

  /**
   * Realiza el login del usuario
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(credentials)
    });
  
    const result = await this.handleResponse<ApiResponse<LoginResponse>>(response);
    
    if (result.success && result.data) {
      this.setToken(result.data.token);
      this.setUser(result.data.user);
      return result.data;
    }
    
    throw new Error(result.message || 'Error en el login');
  }

  /**
   * Cierra la sesión del usuario
   */
  async logout(): Promise<void> {
    try {
      // Intentar hacer logout en el servidor
      await fetch(`${this.baseURL}${this.endpoint}/logout`, {
        method: 'POST',
        headers: this.getHeaders()
      });
    } catch (error) {
      // Continuar con el logout local aunque falle el servidor
      console.warn('Error al hacer logout en el servidor:', error);
    } finally {
      this.clearAuthData();
    }
  }

  /**
   * Verifica si el token actual es válido
   */
  async verifyToken(): Promise<boolean> {
    const token = this.getToken();
    if (!token) return false;

    try {
      const response = await fetch(`${this.baseURL}${this.endpoint}/verify`, {
        method: 'GET',
        headers: this.getHeaders()
      });

      if (response.ok) {
        const result = await this.handleResponse<ApiResponse<User>>(response);
        if (result.success && result.data) {
          this.setUser(result.data);
          return true;
        }
      }
    } catch (error) {
      console.error('Error verificando token:', error);
    }

    this.clearAuthData();
    return false;
  }

  /**
   * Solicita el reset de contraseña enviando un código al email
   */
  async forgotPassword(data: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/forgot-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const result = await this.handleResponse<ApiResponse<ForgotPasswordResponse>>(response);
    
    if (result.success && result.data) {
      return result.data;
    }
    
    throw new Error(result.message || 'Error solicitando reset de contraseña');
  }

  /**
   * Verifica el código de reset de contraseña
   */
  async verifyResetCode(data: VerifyResetCodeRequest): Promise<VerifyResetCodeResponse> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/verify-reset-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const result = await this.handleResponse<ApiResponse<VerifyResetCodeResponse>>(response);
    
    if (result.success && result.data) {
      return result.data;
    }
    
    throw new Error(result.message || 'Código de verificación inválido');
  }

  /**
   * Resetea la contraseña con el token de reset
   */
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const result = await this.handleResponse<ApiResponse<ResetPasswordResponse>>(response);
    
    if (result.success && result.data) {
      return result.data;
    }
    
    throw new Error(result.message || 'Error reseteando contraseña');
  }

  /**
   * Obtiene el token almacenado
   */
  getToken(): string | null {
    return localStorage.getItem(this.TOKEN_KEY);
  }

  /**
   * Obtiene el usuario almacenado
   */
  getUser(): User | null {
    const userStr = localStorage.getItem(this.USER_KEY);
    if (!userStr) return null;
    
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }

  /**
   * Verifica si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    return !!(this.getToken() && this.getUser());
  }

  /**
   * Verifica si el usuario tiene un rol específico
   */
  hasRole(role: Role): boolean {
    const user = this.getUser();
    return user?.role === role;
  }

  /**
   * Verifica si el usuario tiene alguno de los roles especificados
   */
  hasAnyRole(roles: Role[]): boolean {
    const user = this.getUser();
    return user ? roles.includes(user.role) : false;
  }

  /**
   * Obtiene la información actualizada del usuario actual desde el servidor
   */
  async getCurrentUser(): Promise<User> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/verify`, {
      method: 'GET',
      headers: this.getHeaders()
    });

    const result = await this.handleResponse<ApiResponse<User>>(response);
    
    if (result.success && result.data) {
      this.setUser(result.data);
      return result.data;
    }
    
    throw new Error(result.message || 'Error obteniendo usuario actual');
  }

  /**
   * Cambia la contraseña del usuario actual
   */
  async changePassword(data: ChangePasswordRequest): Promise<void> {
    const response = await fetch(`${this.baseURL}${this.endpoint}/change-password`, {
      method: 'POST',
      headers: {
        ...this.getHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });

    const result = await this.handleResponse<ApiResponse<void>>(response);
    
    if (!result.success) {
      throw new Error(result.message || 'Error cambiando contraseña');
    }
  }

  /**
   * Almacena el token en localStorage
   */
  private setToken(token: string): void {
    localStorage.setItem(this.TOKEN_KEY, token);
  }

  /**
   * Almacena el usuario en localStorage
   */
  private setUser(user: User): void {
    localStorage.setItem(this.USER_KEY, JSON.stringify(user));
  }

  /**
   * Limpia todos los datos de autenticación
   */
  private clearAuthData(): void {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(this.USER_KEY);
  }
}

// Exportar instancia singleton
export const authService = new AuthService();
export default authService;